import { observer } from 'mobx-react'
import { useEffect } from 'react'

import _Button from '@/component/atoms/Button/_Button/_Button'
import { _OldButton } from '@/component/atoms/Button/_OldButton'
import { default as Checkbox } from '@/component/atoms/Checkbox/_Checkbox'
import { Dropdown, DropdownItem } from '@/component/molecules/Dropdown'
import { default as Input } from '@/component/molecules/Input'
import { default as PlusMinusInput } from '@/component/molecules/PlusMinusInput'
import { Radio } from '@/component/molecules/Radio'
import { reportController } from '@/controller/report.controller'
import { CommonPrintService, simplePrintService } from '@/service/print'
import WebviewService from '@/service/webview.service'
import appPostMsg from '@/utils/appPostMsg'
import { openLink } from '@/utils/common'
import { commonErrorMessage } from '@/utils/toast'

import S from './Print.style'

type Props = {
  printTargetIds: (number | string)[]
  type?: (typeof simplePrintService)['type']
  title?: string
  options?: { params?: req.OneClickReportParams }
}

export const SimplePrintModal = observer(
  ({ printTargetIds, title = '보고서', type = 'REPORT', options }: Props) => {
    const onPreviewButtonClick = async () => {
      if (!options?.params) return
      const params = options.params
      if (WebviewService.isAppAndroid) {
        appPostMsg.previewOneClickReport(params)
        return
      }

      try {
        const { url, fileExt } = await reportController.downloadOneClick(params)
        if (WebviewService.isIpad) {
          appPostMsg.onPreviewIOS(url, { fileExt, fileName: `${title} 미리보기` })
          return
        }

        openLink(url)
      } catch (e) {
        commonErrorMessage(e)
      }
    }

    useEffect(() => {
      simplePrintService.fetchPrintListInit(printTargetIds, type)

      return () => {
        simplePrintService.uiStore.initData()
      }
    }, [])

    return (
      <section css={[S.Wrap, S.Calculate]}>
        <header>
          <h3>{title} 인쇄</h3>
        </header>
        <div className="print-body">
          <div className="print-row print-count">
            <p className="row-left">매수</p>
            <div className="row-right">
              <PlusMinusInput
                value={simplePrintService.printOption.copyNumber}
                max={CommonPrintService.MAX_PRINT_QUANTITY}
                min={1}
                onChange={(copyNumber) => {
                  simplePrintService.printOption.update({ copyNumber })
                }}
                editable
              />
            </div>
          </div>

          <div className="print-row print-range">
            <p className="row-left">범위</p>
            <div className="row-right">
              <Radio.Group
                className="range-radio"
                value={simplePrintService.uiStore.visibleRangeOption}
                onChange={(event) =>
                  simplePrintService.uiStore.update({
                    visibleRangeOption: event.target.value as 'ALL' | 'SELECT',
                    inputText: '',
                  })
                }
              >
                <Radio value="ALL">전체</Radio>
                <Radio value="SELECT">페이지 선택</Radio>
              </Radio.Group>
              {simplePrintService.uiStore.visibleRangeOption === 'SELECT' && (
                <div>
                  <div className="range-insert-area">
                    <Input
                      type="text"
                      className={simplePrintService.isVisibleRangeError ? 'warning' : ''}
                      placeholder="페이지를 입력해주세요."
                      onChange={(event) =>
                        simplePrintService.uiStore.update({ inputText: event.target.value })
                      }
                    />
                    <p>예시) 1-7</p>
                  </div>
                  {simplePrintService.isVisibleRangeError && (
                    <p className="warning page-error">페이지를 범위에 맞게 입력해주세요.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {simplePrintService.printAgent && (
            <div className="print-row printer-select">
              <p className="row-left">프린터 선택</p>
              <div className="row-right">
                <div className="print-dropdown">
                  <Dropdown
                    height={40}
                    width={344}
                    listHeight={120}
                    value={`${simplePrintService.printAgent.selectedPrinter?.machineId}:${simplePrintService.printAgent?.selectedPrinter?.displayName}`}
                    onChange={(value: string) => {
                      const [mid, printName] = value.split(':')
                      const printer = simplePrintService.printAgent?.printerList.find(
                        ({ displayName, machineId }) =>
                          displayName === printName && machineId === mid,
                      )

                      simplePrintService.selectPrinter(printer!)
                    }}
                  >
                    {simplePrintService.printAgent.printerList.map((printer) => (
                      <DropdownItem
                        key={`${printer.machineId}:${printer.displayName}`}
                        data-mathflat-unique-id={`${printer.machineId}:${printer.displayName}`}
                        value={`${printer.machineId}:${printer.displayName}`}
                      >
                        {printer.displayName}
                      </DropdownItem>
                    ))}
                  </Dropdown>
                </div>
              </div>
            </div>
          )}

          <div className="print-row print-option">
            <p className="row-left">옵션</p>
            <div className="row-right">
              <Checkbox
                onChange={(isDuplex) => simplePrintService.printOption.update({ isDuplex })}
                checked={simplePrintService.printOption.isDuplex}
                text={<span>양면</span>}
              />
              <Checkbox
                onChange={(isMonochrome) => simplePrintService.printOption.update({ isMonochrome })}
                checked={simplePrintService.printOption.isMonochrome}
                text={<span>흑백</span>}
              />
              <Checkbox
                onChange={(isReverse) => simplePrintService.printOption.update({ isReverse })}
                checked={simplePrintService.printOption.isReverse}
                text={<span>역순</span>}
              />
            </div>
          </div>
        </div>
        <div className="print-footer">
          {options?.params && type !== 'CAS_ONE_CLICK_REPORT' && (
            <_OldButton width={103} theme="individual" onClick={onPreviewButtonClick}>
              PDF 미리보기
            </_OldButton>
          )}
          <_Button
            minWidth={144}
            size="m"
            disabled={simplePrintService.disabledPrint}
            theme="primary"
            onClick={() => simplePrintService.onPrint(options)}
          >
            인쇄하기
          </_Button>
        </div>
      </section>
    )
  },
)
